import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

const UpdateUserProfile: React.FC = () => {
  const { userProfile } = useAuth();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'users'));
      const userData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(userData);
      console.log('Users loaded:', userData);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (userId: string, email: string) => {
    setUpdating(true);
    try {
      const isEligible = email?.endsWith('@chimperator-live.de') || 
                        email?.endsWith('@imwizemann.de');
      
      await updateDoc(doc(db, 'users', userId), {
        isEligibleForDiscount: isEligible
      });
      
      console.log(`✅ Updated ${email} with isEligibleForDiscount: ${isEligible}`);
      alert(`✅ Profil aktualisiert!\n${email}\nRabatt-berechtigt: ${isEligible ? 'Ja (30%)' : 'Nein'}`);
      
      // Reload users
      await loadUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      alert('❌ Fehler beim Aktualisieren');
    } finally {
      setUpdating(false);
    }
  };

  const promoteToAdmin = async (userId: string, email: string, currentRole: string) => {
    // Security check: Only @chimperator-live.de can be promoted to admin
    if (!email?.endsWith('@chimperator-live.de')) {
      alert('⚠️ Nur Benutzer mit @chimperator-live.de E-Mail-Adresse können zu Admins befördert werden!');
      return;
    }

    const confirmMsg = currentRole === 'admin'
      ? `Möchten Sie ${email} wirklich zum Customer herabstufen?\n\nDieser Benutzer verliert alle Admin-Rechte.`
      : `Möchten Sie ${email} wirklich zum Admin befördern?\n\nDieser Benutzer erhält volle Admin-Rechte:\n- Zugriff auf alle Bestellungen\n- Zugriff auf alle Kundenaten\n- Zugriff auf Einstellungen\n- Status-Verwaltung`;

    if (!confirm(confirmMsg)) {
      return;
    }

    setUpdating(true);
    try {
      const newRole = currentRole === 'admin' ? 'customer' : 'admin';
      
      await updateDoc(doc(db, 'users', userId), {
        role: newRole
      });
      
      console.log(`✅ Updated ${email} role to: ${newRole}`);
      alert(`✅ Rechte aktualisiert!\n${email}\nNeue Rolle: ${newRole}`);
      
      // Reload users
      await loadUsers();
    } catch (error) {
      console.error('Error updating user role:', error);
      alert('❌ Fehler beim Ändern der Rechte');
    } finally {
      setUpdating(false);
    }
  };

  if (userProfile?.role !== 'admin') {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">Nur für Admins zugänglich</p>
      </div>
    );
  }

  if (loading) {
    return <div className="p-8 text-center">Lädt Benutzer...</div>;
  }

  const chimperatorUsers = users.filter(u => u.email?.endsWith('@chimperator-live.de'));

  return (
    <div className="max-w-6xl mx-auto p-8">
      <h1 className="text-3xl font-bold mb-6">Einstellungen & Benutzerverwaltung</h1>
      
      {/* Rechte-Verwaltung Section */}
      <div className="mb-12">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Rechte-Verwaltung</h2>
            <p className="text-sm text-gray-600 mt-1">
              Verwalten Sie Admin-Rechte für Chimperator Live Mitarbeiter
            </p>
          </div>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-900">
            <strong>⚠️ Sicherheitshinweis:</strong> Nur Benutzer mit einer{' '}
            <strong>@chimperator-live.de</strong> E-Mail-Adresse können zu Admins befördert werden.
            Admins haben vollen Zugriff auf alle Kundendaten und Bestellungen.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    E-Mail
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aktuelle Rolle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aktion
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {chimperatorUsers.map(user => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {user.displayName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        user.role === 'admin'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {user.role === 'admin' ? 'Admin' : 'Customer'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => promoteToAdmin(user.id, user.email, user.role)}
                        disabled={updating}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 ${
                          user.role === 'admin'
                            ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                            : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                      >
                        {user.role === 'admin' ? 'Zu Customer' : 'Zu Admin befördern'}
                      </button>
                    </td>
                  </tr>
                ))}
                {chimperatorUsers.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                      Keine Chimperator Live Benutzer gefunden
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Rabatt-Verwaltung Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Rabatt-Berechtigung</h2>
            <p className="text-sm text-gray-600 mt-1">
              Aktualisieren Sie das Rabatt-Feld für alle Benutzer
            </p>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-900">
            <strong>ℹ️ Info:</strong> Das{' '}
            <code>isEligibleForDiscount</code>-Feld wird automatisch basierend auf der
            E-Mail-Domain gesetzt (@chimperator-live.de und @imwizemann.de erhalten 30% Rabatt).
          </p>
        </div>

        <div className="space-y-4">
        {users.map(user => {
          const isEligible = user.email?.endsWith('@chimperator-live.de') || 
                            user.email?.endsWith('@imwizemann.de');
          const hasField = user.isEligibleForDiscount !== undefined;
          const needsUpdate = !hasField || user.isEligibleForDiscount !== isEligible;

          return (
            <div
              key={user.id}
              className="bg-white border rounded-lg p-4 shadow-sm"
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold text-gray-900">{user.email}</p>
                  <div className="text-sm text-gray-600 mt-1 space-y-1">
                    <p>
                      <strong>Rolle:</strong>{' '}
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        user.role === 'admin' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {user.role}
                      </span>
                    </p>
                    <p>
                      <strong>Rabatt-berechtigt:</strong>{' '}
                      {hasField ? (
                        user.isEligibleForDiscount ? (
                          <span className="text-green-600">✓ Ja (30%)</span>
                        ) : (
                          <span className="text-gray-600">✗ Nein</span>
                        )
                      ) : (
                        <span className="text-orange-600">⚠️ Nicht gesetzt</span>
                      )}
                    </p>
                    <p>
                      <strong>Sollte sein:</strong>{' '}
                      {isEligible ? (
                        <span className="text-green-600">✓ Ja (basierend auf Domain)</span>
                      ) : (
                        <span className="text-gray-600">✗ Nein</span>
                      )}
                    </p>
                  </div>
                </div>
                <div>
                  {needsUpdate && (
                    <button
                      onClick={() => updateUser(user.id, user.email)}
                      disabled={updating}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                    >
                      {updating ? 'Aktualisiere...' : 'Aktualisieren'}
                    </button>
                  )}
                  {!needsUpdate && (
                    <span className="text-green-600 text-sm font-medium">
                      ✓ OK
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        </div>

        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600">
            <strong>Anleitung:</strong> Klicken Sie auf "Aktualisieren" für jeden Benutzer,
            der das <code>isEligibleForDiscount</code>-Feld noch nicht hat oder wo der Wert
            nicht mit der E-Mail-Domain übereinstimmt.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpdateUserProfile;

